import type { ReactNode } from 'react';
import { useCallback, useEffect } from 'react';
import { useRouter } from 'next/router';
import { parseReturnPath } from '@highlight-cp/auth-client/return-path';

import { requiresManualActivation } from 'features/account/onboarding/onboarding-machine/guards';
import { onboardingStateApi } from 'features/account/onboarding/onboarding-state-api';
import { useGetUser } from 'features/account/use-get-user';
import { useHeimdallClient } from 'features/app/heimdall-session';

/**
 * Ends the Heimdall and guest sessions, then redirects to the current origin root so BYOC users
 * stay on their subdomain, even when a sign-out fails.
 */
export function useLogout(): () => Promise<void> {
  const heimdallClient = useHeimdallClient();

  return useCallback(async () => {
    await Promise.allSettled([
      heimdallClient?.signOut(),
      fetch('/api/auth/sign-out', { method: 'POST' }),
    ]);
    window.location.href = `${window.location.origin}/`;
  }, [heimdallClient]);
}

const PUBLIC_URLS = [
  '/',
  '/referral-code',
  '/unsubscribe/[highlighter_guid]',
  '/reject-opt-in/[survey-id]/[highlighter-id]',
  '/signup/[referralCode]',
  '/login',
  '/login-with-credentials',
];

// Error pages render statically, without Heimdall's URL, so they cannot tell who is signed in.
const ERROR_ROUTES = ['/404', '/500', '/_error', '/guest-participation-link-error'];

export function AuthGuard({ children }: { children: ReactNode }) {
  const { asPath, push, query, route } = useRouter();
  const { account, status, isAccountLocked, isUserQuit } = useGetUser();

  // Manual-activation communities (e.g. GHI) leave `account.status` at 'new' forever once
  // onboarding is otherwise complete — they sit in a terminal "awaiting staff activation" state
  // instead of transitioning to 'active' like auto-activating communities do. Only fetch (and
  // share the RTK Query cache with `useGetOnboardingSurveys`/the onboarding machine) when the raw
  // status flag would otherwise send this account back into onboarding.
  const { data: onboardingState } = onboardingStateApi.useGetOnboardingStateQuery(undefined, {
    skip: status !== 'authenticated' || account?.status !== 'new',
  });
  const isAwaitingManualActivation = Boolean(
    onboardingState && requiresManualActivation(onboardingState),
  );

  const needsOnboardingRoute =
    (account?.status === 'new' && !isAwaitingManualActivation) ||
    (account?.status === 'guest' && !account?.tosAgreedAt);

  useEffect(() => {
    if (ERROR_ROUTES.includes(route)) {
      return;
    }

    // A refused role keeps its Heimdall session; the login page says why it can't continue.
    if (
      (status === 'unauthenticated' || status === 'unauthorized') &&
      !PUBLIC_URLS.includes(route)
    ) {
      void push(`/login?redirect=${encodeURIComponent(asPath)}`);
      return;
    }

    if (status !== 'authenticated') {
      return;
    }

    // Redirect into onboarding when the state machine still has work to do.
    // Guests who already agreed to TOS go straight to the app (machine uses isGuest → home);
    // sending them back here would loop /onboarding ↔ /home. Accounts awaiting manual
    // activation are exempt via needsOnboardingRoute above — once excluded there,
    // needsOnboardingRoute is false for them on every route, so no route-level exemption
    // is needed (or safe to add: it would also exempt genuinely incomplete accounts).
    if (needsOnboardingRoute && !route.startsWith('/onboarding') && !route.startsWith('/account')) {
      void push('/onboarding');
      return;
    }

    // if auth'd, we have an account, and on public route; go back to the page login was for, or home
    if (account && !isAccountLocked && !isUserQuit && PUBLIC_URLS.includes(route)) {
      void push(parseReturnPath(query.redirect, '/login') ?? '/home');
      return;
    }

    // if auth'd, we dont have an account (or its locked) and on private route; go to landing page
    if ((!account || isAccountLocked || isUserQuit) && !PUBLIC_URLS.includes(route)) {
      void push('/');
    }
  }, [
    status,
    route,
    asPath,
    query.redirect,
    push,
    account,
    isAccountLocked,
    isUserQuit,
    needsOnboardingRoute,
  ]);

  if (ERROR_ROUTES.includes(route)) {
    return children;
  }

  if (!account && !PUBLIC_URLS.includes(route)) {
    return;
  }

  if (needsOnboardingRoute && !route.startsWith('/onboarding') && !route.startsWith('/account')) {
    return;
  }

  return children;
}
